/**
 * Constructor method for creating instance of a sub-navigation item which is either
 * a link to another page, or product
 *
 * Instance properties
 * - isHeaderNavItem : flag which denotes if the item exists in the top-level nav
 * - domNode : reference to domNode
 * - headerSubNav : reference to the sub-nav the item is contained in
 *
 * @param {Node} domNode DomNode for the subNavItem
 * @param {object} subNavObj Instance of the controlling SubNav
 */
const HeaderSubNavItem = function (domNode, subNavObj) {
  this.isHeaderNavItem = false;
  this.domNode = domNode;
  this.headerSubNav = subNavObj;

  this.keyCode = Object.freeze({
    TAB: 9,
    RETURN: 13,
    ESC: 27,
    SPACE: 32,
    PAGEUP: 33,
    PAGEDOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
  });
};

/*
 * Initializer for a HeaderNav instance
 *
 * Sets all listeners for the subNav item to handle keyboard
 * navigation for each subNavItem
 *
 */
HeaderSubNavItem.prototype.init = function () {
  this.domNode.tabIndex = -1;

  this.domNode.addEventListener("keydown", this.handleKeydown.bind(this));
  this.domNode.addEventListener("click", this.handleClick.bind(this));
  this.domNode.addEventListener("focus", this.handleFocus.bind(this));
  this.domNode.addEventListener("blur", this.handleBlur.bind(this));
  this.domNode.addEventListener("mouseover", this.handleMouseover.bind(this));
  this.domNode.addEventListener("mouseout", this.handleMouseout.bind(this));
};

HeaderSubNavItem.prototype.isExpanded = function () {
  return this.domNode.getAttribute("aria-expanded") === "true";
};

/* EVENT HANDLERS */

HeaderSubNavItem.prototype.handleKeydown = function (event) {
  var tgt = event.currentTarget,
    char = event.key,
    flag = false,
    clickEvent;

  function isPrintableCharacter(str) {
    return str.length === 1 && str.match(/\S/);
  }

  switch (event.keyCode) {
    case this.keyCode.SPACE:
    case this.keyCode.RETURN:
      // Create simulated mouse event to mimic the behavior of ATs
      // and let the event handler handleClick do the housekeeping.
      try {
        clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
        });
      } catch (err) {
        if (document.createEvent) {
          // DOM Level 3 for IE 9+
          clickEvent = document.createEvent("MouseEvents");
          clickEvent.initEvent("click", true, true);
        }
      }
      tgt.dispatchEvent(clickEvent);

      flag = true;
      break;

    case this.keyCode.UP:
      this.headerSubNav.setFocusToPreviousItem(this);
      flag = true;
      break;

    case this.keyCode.DOWN:
      this.headerSubNav.setFocusToNextItem(this);
      flag = true;
      break;

    case this.keyCode.LEFT:
      this.headerSubNav.setFocusToController("previous");
      this.headerSubNav.close(true);
      flag = true;
      break;

    case this.keyCode.RIGHT:
      this.headerSubNav.setFocusToController("next");
      this.headerSubNav.close(true);

      flag = true;
      break;

    case this.keyCode.HOME:
    case this.keyCode.PAGEUP:
      this.headerSubNav.setFocusToFirstItem();
      flag = true;
      break;

    case this.keyCode.END:
    case this.keyCode.PAGEDOWN:
      this.headerSubNav.setFocusToLastItem();
      flag = true;
      break;

    case this.keyCode.ESC:
      this.headerSubNav.setFocusToController();
      this.headerSubNav.close(true);
      flag = true;
      break;

    case this.keyCode.TAB:
      this.headerSubNav.setFocusToController();
      break;

    default:
      if (isPrintableCharacter(char)) {
        this.headerSubNav.setFocusByFirstCharacter(this, char);
        flag = true;
      }
      break;
  }

  if (flag) {
    event.stopPropagation();
    event.preventDefault();
  }
};

HeaderSubNavItem.prototype.setExpanded = function (value) {
  if (value) {
    this.domNode.setAttribute("aria-expanded", "true");
  } else {
    this.domNode.setAttribute("aria-expanded", "false");
  }
};

HeaderSubNavItem.prototype.handleClick = function (event) {
  this.headerSubNav.setFocusToController();
  this.headerSubNav.close(true);
};

HeaderSubNavItem.prototype.handleFocus = function (event) {
  this.headerSubNav.hasFocus = true;
};

HeaderSubNavItem.prototype.handleBlur = function (event) {
  this.headerSubNav.hasFocus = false;
  setTimeout(this.headerSubNav.close.bind(this.headerSubNav, false), 100);
};

HeaderSubNavItem.prototype.handleMouseover = function (event) {
  this.headerSubNav.hasHover = true;
  this.headerSubNav.open();
};

HeaderSubNavItem.prototype.handleMouseout = function (event) {
  this.headerSubNav.hasHover = false;
  setTimeout(this.headerSubNav.close.bind(this.headerSubNav, false), 100);
};
